import { useEffect, useState } from 'react';
import { PenLine, AlertTriangle, Trash2, RefreshCw } from 'lucide-react';
import api from '../../../../services/api';
import QrcAttemptModal from '../../../../components/quiz/QrcAttemptModal';
import QrcClearModal from './QrcClearModal';
import DayTimeline from './DayTimeline';
import { scoreColor, timeOfDay, nbspFr } from './helpers';

/**
 * Section « Réponses rédigées » de l'onglet Résultats : une carte par
 * correction aboutie (qrc-history.php), « Consulter » rouvre le verdict complet.
 * Ne s'affiche pas si l'utilisateur n'a aucune correction dans ce scope.
 * `reloadKey` : toute valeur dont le changement doit recharger la liste
 * (l'historique QCM rechargé, un effacement d'historique…).
 */
export default function QrcHistoryView({ scopeId, scopeColor, reloadKey }) {
    const [items, setItems] = useState(null); // null = chargement
    const [openId, setOpenId] = useState(null);
    const [clearOpen, setClearOpen] = useState(false); // effacer ses corrections (migration 148)

    useEffect(() => {
        if (!scopeId) return;
        let alive = true;
        api.get('/qcm/qrc-history.php', { params: { scope_id: scopeId } })
            .then(({ data }) => { if (alive) setItems(data.data?.items || []); })
            .catch(() => { if (alive) setItems([]); });
        return () => { alive = false; };
    }, [scopeId, reloadKey]);

    if (!items || items.length === 0) return null;

    return (
        <div className="qrc-hist">
            <div className="qrc-hist__head">
                <h3 className="sl-section-title"><PenLine size={16} /> Réponses rédigées</h3>
                <p className="sl-section-sub">
                    {items.length} correction{items.length > 1 ? 's' : ''} rédigée{items.length > 1 ? 's' : ''} — chaque verdict reste consultable.
                </p>
            </div>
            <DayTimeline items={items} dateOf={it => it.created_at} keyOf={it => it.id}
                pctOf={it => (it.score_core_pct ?? it.score_pct ?? 0)}
                unit={n => `${n} correction${n > 1 ? 's' : ''}`}
                renderCard={it => {
                    const pct = it.score_core_pct ?? it.score_pct ?? 0;
                    const col = it.blocked ? '#ef4444' : scoreColor(pct);
                    const state = it.blocked ? 'Bloquée' : it.passed ? 'Acquise' : 'À retravailler';
                    return (
                        <div key={it.id} className="hist-card qrc-hist__card">
                            <span className="hist-card__expand" />
                            <div className="hist-card__main">
                                <div className="hist-card__titleline">
                                    <span className="hist-card__title hist-card__title--clamp" title={it.enonce}>{nbspFr(it.enonce)}</span>
                                    <span className={`qrc-hist__state ${it.blocked ? 'is-blocked' : it.passed ? 'is-ok' : 'is-ko'}`}>
                                        {it.blocked && <AlertTriangle size={11} />} {state}
                                    </span>
                                </div>
                                <div className="hist-card__meta">
                                    {timeOfDay(it.created_at)} · {it.sous_sujet_label}{it.matiere ? ` · ${it.matiere}` : ''}
                                    {it.origin === 'audit' ? ' · audit' : ''}
                                </div>
                                {it.newer_bareme && (
                                    <span className="qrc-hist__newer" title="Le barème de cette question a été précisé depuis ta correction. Ce résultat reste celui de ta réponse d’origine.">
                                        <RefreshCw size={11} /> Nouvelle version de correction disponible
                                    </span>
                                )}
                            </div>
                            <div className="hist-card__score">
                                <span className="hist-card__pct" style={{ color: col }}>{pct}%</span>
                                <span className="hist-card__frac">seuil {it.seuil} %</span>
                            </div>
                            <div className="hist-card__actions">
                                <button type="button" className="hist-btn hist-btn--accent" onClick={() => setOpenId(it.id)}>Consulter</button>
                            </div>
                        </div>
                    );
                }} />
            {/* Repartir de zéro sur les réponses rédigées, sans toucher à l'historique de quiz */}
            <div style={{ textAlign: 'center', marginTop: '18px' }}>
                <button
                    className="btn btn-sm btn-outline"
                    style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
                    onClick={() => setClearOpen(true)}
                >
                    <Trash2 size={14} /> Effacer mes corrections rédigées
                </button>
            </div>
            {openId && <QrcAttemptModal attemptId={openId} accentColor={scopeColor} onClose={() => setOpenId(null)} />}
            {clearOpen && (
                <QrcClearModal
                    scopeId={scopeId} count={items.length}
                    onClose={() => setClearOpen(false)}
                    onCleared={() => setItems([])}
                />
            )}
        </div>
    );
}
